import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeftRight,
  Boxes,
  ClipboardCheck,
  CircleDollarSign,
  FileBarChart2,
  Layers,
  Loader2,
  Package,
  PackagePlus,
  Plus,
  Settings2,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBalances,
  useInventoryConfig,
  useMovements,
  useUpdateInventoryConfig,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useActiveCompany } from "@/lib/company-context";
import { StatCard } from "@/components/inventory/StatCard";
import { MovementKindBadge } from "@/components/inventory/MovementKindBadge";
import { InventoryEmptyState } from "@/components/inventory/InventoryEmptyState";
import { fmtQty, fmtRelative } from "@/components/inventory/format";

export const Route = createFileRoute("/companies/$companyId/inventario/")({
  component: InventarioIndexPage,
});

function InventarioIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <InventarioIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function InventarioIndexContent() {
  const fmtCop = useMoneyFormatter();
  const fmtCop2 = useMoneyFormatter();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [warehouseFilter, setWarehouseFilter] = useState<string>("");

  const balancesQuery = useBalances(companyId, {
    warehouseId: warehouseFilter || undefined,
    pageSize: 200,
  });
  const warehousesQuery = useWarehouses(companyId);
  const configQuery = useInventoryConfig(companyId);
  const recentQuery = useMovements(companyId, { pageSize: 5 });
  const updateConfig = useUpdateInventoryConfig(companyId);

  const balances = useMemo(
    () => balancesQuery.data?.balances ?? [],
    [balancesQuery.data?.balances],
  );
  const warehouses = useMemo(
    () => warehousesQuery.data?.warehouses ?? [],
    [warehousesQuery.data?.warehouses],
  );
  const recent = recentQuery.data?.movements ?? [];

  const totals = useMemo(() => {
    const totalValue = balances.reduce((s, b) => s + b.totalValue, 0);
    const productIds = new Set(balances.map((b) => b.productId));
    const usedWarehouses = new Set(balances.map((b) => b.warehouseId));
    return {
      totalValue,
      productCount: productIds.size,
      warehouseCount: usedWarehouses.size,
      activeWarehouses: warehouses.filter((w) => w.isActive).length,
    };
  }, [balances, warehouses]);

  const valueByWarehouse = useMemo(() => {
    const m = new Map<
      string,
      { code: string; name: string; value: number; lines: number }
    >();
    for (const b of balances) {
      const cur = m.get(b.warehouseId);
      if (cur) {
        cur.value += b.totalValue;
        cur.lines += 1;
      } else {
        m.set(b.warehouseId, {
          code: b.warehouseCode,
          name: b.warehouseName,
          value: b.totalValue,
          lines: 1,
        });
      }
    }
    return Array.from(m.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.value - a.value);
  }, [balances]);

  const isPending =
    balancesQuery.isPending ||
    warehousesQuery.isPending ||
    configQuery.isPending;

  const isEmpty = !isPending && balances.length === 0 && recent.length === 0;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a la empresa
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Boxes className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Inventario
            </h1>
            <Badge variant="muted" className="text-[10px] uppercase">
              Sistema permanente
            </Badge>
            <Badge variant="muted" className="text-[10px] uppercase">
              Promedio ponderado
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Saldos en vivo, kardex auditable y conteos físicos con asientos
            contables automáticos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link
              to="/companies/$companyId/inventario/movimientos"
              params={{ companyId }}
            >
              <Layers className="h-4 w-4" />
              Ver kardex
            </Link>
          </Button>
          <Button asChild>
            <Link
              to="/companies/$companyId/inventario/movimientos/new"
              params={{ companyId }}
            >
              <Plus className="h-4 w-4" />
              Nuevo movimiento
            </Link>
          </Button>
        </div>
      </header>

      {/* ── KPIs ────────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Valor total"
          value={isPending ? "—" : fmtCop(totals.totalValue)}
          icon={<CircleDollarSign className="h-4 w-4" />}
          tone="primary"
          hint={
            isPending
              ? null
              : warehouseFilter
                ? "Bodega filtrada"
                : "Todas las bodegas"
          }
        />
        <StatCard
          label="Productos en stock"
          value={isPending ? "—" : totals.productCount}
          icon={<Package className="h-4 w-4" />}
          tone="default"
          hint={
            isPending
              ? null
              : `${balances.length} línea${balances.length === 1 ? "" : "s"} de saldo`
          }
        />
        <StatCard
          label="Bodegas activas"
          value={isPending ? "—" : totals.activeWarehouses}
          icon={<WarehouseIcon className="h-4 w-4" />}
          tone="default"
          hint={
            isPending ? null : (
              <Link
                to="/companies/$companyId/inventario/bodegas"
                params={{ companyId }}
                className="text-primary hover:underline"
              >
                Administrar →
              </Link>
            )
          }
        />
        <StatCard
          label="Saldos negativos"
          value={
            configQuery.data?.allowNegativeStock ? "Permitidos" : "Bloqueados"
          }
          icon={<Settings2 className="h-4 w-4" />}
          tone={configQuery.data?.allowNegativeStock ? "warning" : "default"}
          hint={
            configQuery.data?.allowNegativeStock
              ? "Las salidas pueden dejar el saldo en negativo"
              : "Las salidas se bloquean al llegar a 0"
          }
          action={
            <button
              type="button"
              disabled={updateConfig.isPending || !configQuery.data}
              onClick={() => {
                if (!configQuery.data) return;
                updateConfig.mutate(
                  {
                    allowNegativeStock: !configQuery.data.allowNegativeStock,
                  },
                  {
                    onSuccess: () => toast.success("Configuración actualizada"),
                    onError: (err) =>
                      toast.error("No pudimos actualizar", {
                        description: err.message,
                      }),
                  },
                );
              }}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {updateConfig.isPending
                ? "Actualizando…"
                : configQuery.data?.allowNegativeStock
                  ? "Bloquear"
                  : "Permitir"}
            </button>
          }
        />
      </div>

      {/* ── Quick actions ───────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <Link
            to="/companies/$companyId/inventario/bodegas"
            params={{ companyId }}
          >
            <WarehouseIcon className="h-4 w-4" />
            Bodegas
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link
            to="/companies/$companyId/inventario/traslados/new"
            params={{ companyId }}
          >
            <ArrowLeftRight className="h-4 w-4" />
            Traslado
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link
            to="/companies/$companyId/inventario/conteos"
            params={{ companyId }}
          >
            <ClipboardCheck className="h-4 w-4" />
            Conteos físicos
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link
            to="/companies/$companyId/inventario/reportes"
            params={{ companyId }}
          >
            <FileBarChart2 className="h-4 w-4" />
            Reportes
          </Link>
        </Button>
      </div>

      {isEmpty ? (
        <InventoryEmptyState companyId={companyId} needsStockableProduct />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* ── Balances table (2/3 width on desktop) ───────────────── */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="text-base">
                  Existencias actuales
                </CardTitle>
                <CardDescription>
                  Saldo en vivo por producto y bodega.
                </CardDescription>
              </div>
              <select
                className="rounded border bg-background px-2 py-1.5 text-sm"
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
              >
                <option value="">Todas las bodegas</option>
                {warehouses
                  .filter((w) => w.isActive)
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} — {w.name}
                    </option>
                  ))}
              </select>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {balancesQuery.isPending ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : balancesQuery.isError ? (
                <ErrorState
                  title="No pudimos cargar las existencias"
                  description={balancesQuery.error?.message ?? ""}
                  onRetry={() => void balancesQuery.refetch()}
                  retryLabel="Reintentar"
                />
              ) : balances.length === 0 ? (
                <p className="px-6 py-8 text-center text-sm text-muted-foreground">
                  Sin existencias
                  {warehouseFilter ? " en esta bodega" : " todavía"}.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Bodega</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Costo prom.</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {balances.slice(0, 50).map((b) => (
                      <TableRow key={`${b.productId}:${b.warehouseId}`}>
                        <TableCell>
                          <Link
                            to="/companies/$companyId/productos/$productId"
                            params={{ companyId, productId: b.productId }}
                            className="font-medium hover:underline"
                          >
                            {b.productName}
                          </Link>
                          {b.productSku && (
                            <span className="ml-2 font-mono text-xs text-muted-foreground">
                              {b.productSku}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="muted" className="font-mono">
                            {b.warehouseCode}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${
                            b.qty < 0 ? "text-destructive" : ""
                          }`}
                        >
                          {fmtQty(b.qty)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {fmtCop2(b.avgUnitCost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {fmtCop(b.totalValue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {balances.length > 50 && (
                <div className="flex items-center justify-between border-t px-6 py-3 text-xs text-muted-foreground">
                  <span>
                    Mostrando 50 de {balances.length} líneas. Usa el filtro o el
                    reporte de existencias para ver todo.
                  </span>
                  <Button asChild variant="ghost" size="sm">
                    <Link
                      to="/companies/$companyId/inventario/reportes"
                      params={{ companyId }}
                    >
                      Reporte completo
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Side rail: warehouse breakdown + recent activity ─────── */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Valor por bodega</CardTitle>
                <CardDescription>
                  Distribución del valor en stock.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {valueByWarehouse.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Sin valor en stock.
                  </p>
                ) : (
                  valueByWarehouse.map((w) => {
                    const pct =
                      totals.totalValue > 0
                        ? (w.value / totals.totalValue) * 100
                        : 0;
                    return (
                      <div key={w.id} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div className="flex min-w-0 items-center gap-2">
                            <Badge
                              variant="muted"
                              className="font-mono text-[10px]"
                            >
                              {w.code}
                            </Badge>
                            <span className="truncate text-muted-foreground">
                              {w.name}
                            </span>
                          </div>
                          <span className="tabular-nums font-medium">
                            {fmtCop(w.value)}
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">
                    Actividad reciente
                  </CardTitle>
                  <CardDescription>Últimos 5 movimientos.</CardDescription>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link
                    to="/companies/$companyId/inventario/movimientos"
                    params={{ companyId }}
                  >
                    Ver todo
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {recentQuery.isPending ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : recent.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-4 text-center">
                    <PackagePlus className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Aún no hay movimientos.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y">
                    {recent.map((m) => (
                      <li
                        key={m.id}
                        className="flex items-start gap-3 py-2.5 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <MovementKindBadge kind={m.kind} short />
                            <Badge
                              variant="muted"
                              className="font-mono text-[10px]"
                            >
                              {m.warehouseCode ?? "?"}
                            </Badge>
                          </div>
                          <p className="mt-1 truncate font-medium">
                            {m.productName ?? "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {fmtRelative(m.createdAt)}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="tabular-nums">
                            {m.direction === "in" ? "+" : "−"}
                            {fmtQty(m.qty)}
                          </div>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {fmtCop(m.totalCost)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {balances.some((b) => b.qty < 0) && (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <CardContent className="flex items-start gap-2 py-3 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="space-y-1">
                    <p className="font-medium text-amber-900 dark:text-amber-200">
                      Hay saldos en negativo
                    </p>
                    <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                      Algún producto tiene cantidad menor a cero. Registra una
                      compra o un ajuste para regularizar.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
